import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Switch,
  Platform,
  Alert,
  TextInput,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { auth, EmailAuthProvider } from '../config/firebase';
import type { User, UserCredential } from 'firebase/auth';
const updateEmail = (user: User, email: string) => user.updateEmail(email);
const updatePassword = (user: User, password: string) => user.updatePassword(password);
const reauthenticateWithCredential = (user: User, credential: any) => user.reauthenticateWithCredential(credential);
import { useTheme } from '../context/ThemeContext';
import { createThemedStyles } from '../theme/useThemedStyles';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useSubscription } from '../context/SubscriptionContext';
import { getSafeRNBlobUtil, isWeb } from '../utils/platformUtils';
import {
  authenticateWithBiometrics,
  getBiometricErrorMessage,
} from '../utils/biometricAuth';
import CollapsibleSection from '../components/CollapsibleSection';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useInterstitialAd } from '../hooks/useInterstitialAd';
import { hasDeveloperAccess, getTestingAccountInfo } from '../config/testing';
import { useGoals } from '../context/GoalsContext';
import { useIncome } from '../context/IncomeContext';
import { useSharedStyles } from '../utils/styles';
import AdBanner from '../components/AdBanner';
import WebPage from '../components/web/WebPage';
import PageScroll from '../components/web/PageScroll';
import AppModal from '../components/web/AppModal';
import { webModalFooterStyle } from '../theme/webModal';
import ThemeModePicker from '../components/ThemeModePicker';
import ThemeColorPicker from '../components/ThemeColorPicker';
import Constants from 'expo-constants';

const APP_VERSION = Constants.expoConfig?.version ?? '1.1.4';

type RootStackParamList = {
  DashboardMain: undefined;
  Settings: undefined;
  FAQ: undefined;
  TermsPrivacy: undefined;
  Subscription: undefined;
  Auth: undefined;
  NotificationTest: undefined;
};

type SettingsScreenNavigationProp = StackNavigationProp<RootStackParamList>;

// Only import FileSystem and Sharing for native platforms
const FileSystemModule = Platform.select({
  native: () => require('expo-file-system'),
  default: () => null,
})();

const SharingModule = Platform.select({
  native: () => require('expo-sharing'),
  default: () => null,
})();

const useSettingsLocalStyles = createThemedStyles((colors) => ({
  headerLogoutButton: {
    marginRight: Platform.OS === 'web' ? 8 : 16,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: colors.error.main,
  },
  headerLogoutText: {
    color: colors.text.onDark,
    fontSize: 14,
    fontWeight: '600',
  },
  securitySwitchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 14,
    marginBottom: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  securitySwitchLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.text.primary,
    flex: 1,
    paddingRight: 12,
  },
}));

const SettingsScreen = () => {
  const styles = useSharedStyles();
  const { colors } = useTheme();
  const settingsLocalStyles = useSettingsLocalStyles();
  // Editable name field: use local state for input, sync to Firebase on save
  const { user, logout, deleteAccount, refreshUser } = useAuth();
  // Use nameInput as the single source of truth for display name
  const [nameInput, setNameInput] = React.useState(
    settings?.profile?.displayName || user?.displayName || ''
  );
  const [profileSaving, setProfileSaving] = useState(false);
  const [pendingName, setPendingName] = useState<string | null>(null);
  
  // Initialize nameInput once when user loads
  useEffect(() => {
    const currentDisplayName = settings?.profile?.displayName || user?.displayName;
    if (currentDisplayName) {
      setNameInput(currentDisplayName);
    }
  }, [user?.displayName, settings?.profile?.displayName]);

  const {
    settings,
    updateProfile,
    updateCurrency,
    updateDateFormat,
    updateIncomeSettings,
    updateNotifications,
    updateSecurity,
    clearPin,
    exportData,
  } = useSettings();
  const { 
    subscription, 
    canExportData, 
    getExportFormats,
    isDeveloperMode,
    setDeveloperMode,
    setTestTier,
    setTestTrial,
    resetToFree,
    currentTier,
    shouldShowAds
  } = useSubscription();
  const navigation = useNavigation<SettingsScreenNavigationProp>();

  // Show interstitial ad when screen comes into focus (rate limited to every 10 minutes)
  useInterstitialAd();

  // Always use settings context for name so UI updates immediately after save
  const name = settings?.profile?.displayName || user?.displayName || '';
  const email = user?.email || '';

  // Keep local name/email in sync with user changes
  // No local state needed for name/email, always use user context
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [showCustomCurrencyModal, setShowCustomCurrencyModal] = useState(false);
  const [customCurrencyCode, setCustomCurrencyCode] = useState('');
  const [customCurrencySymbol, setCustomCurrencySymbol] = useState('');
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showProfileSuccess, setShowProfileSuccess] = useState(false);
  const [showPasswordSuccess, setShowPasswordSuccess] = useState(false);
  const [showPinSuccess, setShowPinSuccess] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  // Modal for re-authentication when updating email
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [reauthLoading, setReauthLoading] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0); // Force re-render
  const [localBiometric, setLocalBiometric] = useState(settings.security.biometric); // Local state for immediate UI updates
  const [localAppLock, setLocalAppLock] = useState(settings.security.appLock);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const exportSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showExportSuccessMessage = useCallback((format: string) => {
    if (exportSuccessTimeoutRef.current) {
      clearTimeout(exportSuccessTimeoutRef.current);
    }
    setSuccessMessage(`${format.toUpperCase()} exported successfully`);
    exportSuccessTimeoutRef.current = setTimeout(() => {
      setSuccessMessage(null);
      exportSuccessTimeoutRef.current = null;
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (exportSuccessTimeoutRef.current) {
        clearTimeout(exportSuccessTimeoutRef.current);
      }
    };
  }, []);
  const [emailVerified, setEmailVerified] = useState(user?.emailVerified ?? false);
  const [resendLoading, setResendLoading] = useState(false);

  // Check if current user has developer access
  const hasAccess = hasDeveloperAccess(user?.email, user?.uid);
  const testingAccountInfo = getTestingAccountInfo(user?.email);

  // Update local state when user data changes
  useEffect(() => {
    if (user) {
      setEmailVerified(user.emailVerified ?? false);
    }
  }, [user]);

  // Force re-render when settings change
  useEffect(() => {
    setForceUpdate((prev: number) => prev + 1);
  }, [settings.security.biometric]);

  // Sync local state with context state
  useEffect(() => {
    setLocalBiometric(settings.security.biometric);
  }, [settings.security.biometric]);

  useEffect(() => {
    setLocalAppLock(settings.security.appLock);
  }, [settings.security.appLock]);

  const currencyOptions = [
    { label: 'USD ($)', value: { code: 'USD', symbol: '$' } },
    { label: 'EUR (€)', value: { code: 'EUR', symbol: '€' } },
    { label: 'GBP (£)', value: { code: 'GBP', symbol: '£' } },
    { label: 'CNY (¥)', value: { code: 'CNY', symbol: '¥' } },
    { label: 'JPY (¥)', value: { code: 'JPY', symbol: '¥' } },
    { label: 'INR (₹)', value: { code: 'INR', symbol: '₹' } },
    { label: 'Custom...', value: { code: 'CUSTOM', symbol: '' } },
  ];

  const dateFormatOptions = [
    { label: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
    { label: 'DD/MM/YYYY', value: 'DD/MM/YYYY' },
    { label: 'YYYY-MM-DD', value: 'YYYY-MM-DD' },
  ];

  const incomeTypeOptions = [
    { label: 'Monthly', value: 'monthly' },
    { label: 'Bi-Weekly', value: 'bi-weekly' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Daily', value: 'daily' },
  ] as const;

  const handleResendVerification = async () => {
    if (!user) return;
    setResendLoading(true);
    try {
      await user.sendEmailVerification();
      Alert.alert('Verification Email Sent', 'A verification email has been sent to your address. Please check your inbox.');
    } catch (error) {
      Alert.alert('Error', 'Failed to send verification email.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    if (!user) return;
    try {
      await user.reload();
      setEmailVerified(user.emailVerified ?? false);
      // Remove refreshUser call to prevent logout issues
      // if (refreshUser) await refreshUser();
    } catch (error) {
      console.error('Error checking verification status:', error);
    }
  };

  // Helper for re-auth and retry email update
  const handleReauthAndUpdateEmail = async () => {
    if (!user || !pendingEmail) return;
    if (!reauthPassword) {
      Alert.alert('Error', 'Password is required to re-authenticate.');
      return;
    }
    setReauthLoading(true);
    try {
      console.log('[Reauth] Attempting re-authentication for email update', { currentEmail: user.email, pendingEmail });
      const credential = EmailAuthProvider.credential(user.email || '', reauthPassword);
      await reauthenticateWithCredential(user, credential);
      // Reload user after re-auth
      await user.reload();
      if (refreshUser) await refreshUser();
      // Try updating email again
      await updateEmail(user, pendingEmail);
      await user.sendEmailVerification();
      Alert.alert(
        'Verify Your Email',
        'A verification email has been sent to your new address. Please verify your email to complete the update.'
      );
      if (refreshUser) {
        await refreshUser();
      }
      await updateProfile({ displayName: name, email: pendingEmail });
      setShowProfileSuccess(true);
      setTimeout(() => setShowProfileSuccess(false), 3000);
      setEmailVerified(user.emailVerified ?? false);
      setShowReauthModal(false);
      setPendingEmail(null);
      setReauthPassword('');
    } catch (reauthError: any) {
      console.error('[Reauth] Error during re-auth or email update:', reauthError);
      let reauthMessage = 'Failed to re-authenticate or update email.';
      if (reauthError.code === 'auth/wrong-password') {
        reauthMessage = 'Incorrect password.';
      } else if (reauthError.code === 'auth/invalid-email') {
        reauthMessage = 'Invalid email address.';
      } else if (reauthError.code === 'auth/email-already-in-use') {
        reauthMessage = 'This email is already in use.';
      } else if (reauthError.code) {
        reauthMessage += ` (${reauthError.code})`;
      } else if (reauthError.message) {
        reauthMessage += ` (${reauthError.message})`;
      }
      Alert.alert('Error', reauthMessage);
    } finally {
      setReauthLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (profileSaving) return;
    setProfileSaving(true);
    try {
      if (Platform.OS === 'web') {
        if (auth.currentUser) {
          await auth.currentUser.updateProfile({ displayName: nameInput });
          await auth.currentUser.reload();
          await updateProfile({ displayName: nameInput });
          setShowProfileSuccess(true);
          setTimeout(() => setShowProfileSuccess(false), 3000);
        } else {
          Alert.alert('Error', 'No authenticated user found.');
          setProfileSaving(false);
          return;
        }
      } else {
        if (user) {
          await user.updateProfile({ displayName: nameInput });
          await user.reload();
          await updateProfile({ displayName: nameInput });
          setShowProfileSuccess(true);
          setTimeout(() => setShowProfileSuccess(false), 3000);
        } else {
          Alert.alert('Error', 'No authenticated user found.');
          setProfileSaving(false);
          return;
        }
      }
    } catch (error: any) {
      console.error('Profile update error:', error);
      Alert.alert('Error', error?.message || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email || !currentPassword || !newPassword) {
      Alert.alert('Error', 'Please enter both current and new password');
      return;
    }

    try {
      // First reauthenticate
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Then update password
      await updatePassword(user, newPassword);

      setShowPasswordSuccess(true);
      // Hide success message after 3 seconds
      setTimeout(() => setShowPasswordSuccess(false), 3000);
      
      setCurrentPassword('');
      setNewPassword('');
    } catch (error: any) {
      console.error('Reset password error:', error);
      let errorMessage = 'Failed to update password';
      
      switch (error.code) {
        case 'auth/wrong-password':
          errorMessage = 'Current password is incorrect';
          break;
        case 'auth/weak-password':
          errorMessage = 'New password is too weak. It should be at least 6 characters long.';
          break;
        case 'auth/requires-recent-login':
          errorMessage = 'Please log out and log in again to change your password';
          break;
        default:
          errorMessage = error.message;
      }
      
      Alert.alert('Error', errorMessage);
    }
  };

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
      Alert.alert('Error', 'Failed to log out. Please try again.');
    }
  }, [logout]);

  const signOutHeaderAction = isWeb ? (
    <TouchableOpacity
      onPress={handleLogout}
      style={settingsLocalStyles.headerLogoutButton}
      accessibilityRole="button"
      accessibilityLabel="Sign out"
    >
      <Text style={settingsLocalStyles.headerLogoutText}>Sign Out</Text>
    </TouchableOpacity>
  ) : undefined;

  useLayoutEffect(() => {
    if (isWeb) return;
    navigation.setOptions({
      headerRight: () => (
        <TouchableOpacity
          onPress={handleLogout}
          style={settingsLocalStyles.headerLogoutButton}
          accessibilityRole="button"
          accessibilityLabel="Log out"
        >
          <Text style={settingsLocalStyles.headerLogoutText}>Logout</Text>
        </TouchableOpacity>
      ),
    });
  }, [navigation, handleLogout]);

  const handleExportData = async (format: 'csv' | 'xlsx' | 'pdf') => {
    try {
      const data = await exportData(format);
      
      const fileName = `goalmint_export.${format}`;
      const mimeType = {
        'csv': 'text/csv',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'pdf': 'application/pdf',
      }[format];

      if (Platform.OS === 'web') {
        // Web platform: Only trigger download for CSV/XLSX, not PDF (PDF handled in context)
        if (format === 'pdf') {
          // Only show success message if PDF was handled by jsPDF, and do not trigger any download
          if (data === '__PDF_HANDLED__') {
            showExportSuccessMessage(format);
            return;
          }
        }
        let content: any;
        let actualMimeType = mimeType;
        let actualFileName = fileName;
        if (format === 'xlsx') {
          // For web, Excel export returns tab-separated data
          content = data;
          actualMimeType = 'text/tab-separated-values';
          actualFileName = 'goalmint_export.xls';
        } else if (format === 'csv') {
          content = data;
          actualMimeType = 'text/csv; charset=utf-8';
          actualFileName = 'goalmint_export.csv';
        } else {
          content = data;
        }
        const blob = new Blob([content], { type: actualMimeType } as any);
        const url = URL.createObjectURL(blob);
        // Create download link for web
        const link = (global as any).document.createElement('a');
        link.href = url;
        link.download = actualFileName;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        // Native platforms: Use FileSystem and platform-specific sharing
        const RNBlobUtil = await getSafeRNBlobUtil();
        if (!RNBlobUtil) {
          throw new Error('File system not available on this platform');
        }
        
        if (Platform.OS === 'android') {
          // For Android, use Downloads directory and add to downloads
          const downloadsPath = `${RNBlobUtil.fs.dirs.DownloadDir}/${fileName}`;
          
          if (format === 'xlsx') {
            // Write base64 data for Excel
            await RNBlobUtil.fs.writeFile(downloadsPath, data, 'base64');
          } else if (format === 'pdf') {
            if (await RNBlobUtil.fs.exists(downloadsPath)) {
              await RNBlobUtil.fs.unlink(downloadsPath);
            }
            await RNBlobUtil.fs.cp(data, downloadsPath);
          } else {
            // For CSV, write as text
            await RNBlobUtil.fs.writeFile(downloadsPath, data, 'utf8');
          }

          // Add to Android downloads and show notification
          await RNBlobUtil.android.addCompleteDownload({
            title: fileName,
            description: `GoalMint ${format.toUpperCase()} Export`,
            mime: mimeType,
            path: downloadsPath,
            showNotification: true,
          });
          
          // Try to open the file with appropriate app
          try {
            await RNBlobUtil.android.actionViewIntent(downloadsPath, mimeType);
          } catch (viewError) {
            // File was saved to downloads successfully
            console.log('File saved to downloads:', downloadsPath);
          }
          
        } else if (Platform.OS === 'ios') {
          // For iOS, use the cache directory and share
          const cachePath = `${RNBlobUtil.fs.dirs.CacheDir}/${fileName}`;

          if (format === 'xlsx') {
            if (await RNBlobUtil.fs.exists(cachePath)) {
              await RNBlobUtil.fs.unlink(cachePath);
            }
            await RNBlobUtil.fs.writeFile(cachePath, data, 'base64');
          } else if (format === 'pdf') {
            const sourcePath = data.replace(/^file:\/\//, '');
            const cacheFilePath = `${RNBlobUtil.fs.dirs.CacheDir}/goalmint_report.pdf`;

            if (sourcePath !== cacheFilePath) {
              if (await RNBlobUtil.fs.exists(cacheFilePath)) {
                await RNBlobUtil.fs.unlink(cacheFilePath);
              }
              await RNBlobUtil.fs.cp(sourcePath, cacheFilePath);
            }

            await RNBlobUtil.ios.previewDocument(cacheFilePath);
            return;
          } else {
            if (await RNBlobUtil.fs.exists(cachePath)) {
              await RNBlobUtil.fs.unlink(cachePath);
            }
            await RNBlobUtil.fs.writeFile(cachePath, data, 'utf8');
          }

          await RNBlobUtil.ios.previewDocument(cachePath);
        }
      }
      
      // Only show success message for non-PDF exports on iOS
      if (!(Platform.OS === 'ios' && format === 'pdf')) {
        showExportSuccessMessage(format);
      }
    } catch (error) {
      console.error('Export error:', error);
      Alert.alert('Export Error', `Failed to export ${format.toUpperCase()} data. Please try again.`);
    }
  };

  const handleBiometricToggle = async (enabled: boolean) => {
    if (Platform.OS === 'web') {
      Alert.alert(
        'Not Available',
        'Fingerprint unlock is only available on the iOS and Android apps.'
      );
      return;
    }

    if (enabled) {
      setLocalBiometric(true);
      setLocalAppLock(false);
      await handleEnableBiometric();
    } else {
      setLocalBiometric(false);
      await updateSecurity({ biometric: false });
    }
  };

  const handleEnableBiometric = async () => {
    try {
      const result = await authenticateWithBiometrics(
        'Authenticate to enable fingerprint unlock'
      );

      if (result.success) {
        await updateSecurity({ biometric: true, appLock: false });
      } else {
        setLocalBiometric(false);
        const message = getBiometricErrorMessage(result.error, result.warning);
        if (message) {
          Alert.alert('Error', message);
        }
      }
    } catch (error) {
      setLocalBiometric(false);
      console.error('Biometric enable error:', error);
      Alert.alert('Error', 'Failed to enable fingerprint unlock. Please try again.');
    }
  };

  const handleCustomCurrencySubmit = () => {
    if (!customCurrencyCode || !customCurrencySymbol) {
      Alert.alert('Invalid Input', 'Please enter both currency code and symbol');
      return;
    }
    
    if (customCurrencyCode.length !== 3) {
      Alert.alert('Invalid Currency Code', 'Currency code must be exactly 3 characters (e.g., USD)');
      return;
    }

    updateCurrency(customCurrencyCode.toUpperCase(), customCurrencySymbol);
    setShowCustomCurrencyModal(false);
    setCustomCurrencyCode('');
    setCustomCurrencySymbol('');
  };

  const handleDeleteAccount = async () => {
    if (!deleteAccountPassword.trim()) {
      Alert.alert('Error', 'Please enter your password');
      return;
    }

    try {
      setIsDeleting(true);
      await deleteAccount(deleteAccountPassword);
      setShowDeleteAccountModal(false);
      // The user will be automatically logged out after account deletion
    } catch (error: any) {
      Alert.alert('Error', error.message);
    } finally {
      setIsDeleting(false);
      setDeleteAccountPassword('');
    }
  };

  const handleSetPin = async () => {
    if (pinCode.length < 4) {
      Alert.alert('Error', 'PIN must be at least 4 digits');
      return;
    }
    try {
      await updateSecurity({ pinCode, appLock: true, biometric: false });
      setLocalAppLock(true);
      setLocalBiometric(false);
      setShowPinSuccess(true);
      setShowPinModal(false);
      setPinCode('');
      setTimeout(() => setShowPinSuccess(false), 3000);
    } catch (error) {
      console.error('Set PIN error:', error);
      Alert.alert('Error', 'Failed to set PIN. Please try again.');
    }
  };

  const closePinModal = () => {
    setShowPinModal(false);
    setPinCode('');
    if (localAppLock && !settings.security.pinCode) {
      setLocalAppLock(false);
    }
  };

  const handleAppLockToggle = async (enabled: boolean) => {
    if (enabled) {
      setLocalAppLock(true);
      setLocalBiometric(false);
      if (!settings.security.pinCode) {
        setShowPinModal(true);
      } else {
        await updateSecurity({ appLock: true, biometric: false });
      }
    } else {
      setLocalAppLock(false);
      await updateSecurity({ appLock: false });
    }
  };

  // Developer mode functions
  const handleToggleDeveloperMode = () => {
    setDeveloperMode(!isDeveloperMode);
  };

  const handleSetTestTier = async (tier: 'free' | 'standard' | 'premium') => {
    try {
      await setTestTier(tier);
    } catch (error) {
      console.error('Error setting test tier:', error);
    }
  };

  const handleSetTestTrial = async (tier: 'standard' | 'premium') => {
    try {
      await setTestTrial(tier);
    } catch (error) {
      console.error('Error setting test trial:', error);
    }
  };

  const handleResetToFree = async () => {
    try {
      await resetToFree();
    } catch (error) {
      console.error('Error resetting to free:', error);
    }
  };

  return (
    <WebPage title="Settings" headerAction={signOutHeaderAction}>
    <PageScroll style={styles.container}>
      <CollapsibleSection title="Profile">
        <View style={styles.inputContainer}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Name</Text>
            <TextInput
              style={styles.input}
              value={nameInput}
              onChangeText={setNameInput}
              placeholder="Enter your name"
              autoCapitalize="words"
              autoCorrect={false}
              returnKeyType="done"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={[styles.input, { backgroundColor: colors.grey[100], color: colors.text.secondary }]}
              value={email}
              editable={false}
              selectTextOnFocus={false}
              placeholder="Email"
              keyboardType="email-address"
              autoCapitalize="none"
            />
            {testingAccountInfo && (
              <Text style={{ color: '#757575', fontSize: 14, textAlign: 'right', marginTop: 2 }}>
                {testingAccountInfo}
              </Text>
            )}
          </View>

          {/* Email Verification Status */}
          {user && (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8, marginBottom: 8 }}>
              <Ionicons
                name={emailVerified ? 'checkmark-circle' : 'alert-circle'}
                size={18}
                color={emailVerified ? colors.success.main : colors.warning.main}
                style={{ marginRight: 6 }}
              />
              <Text style={{ color: emailVerified ? colors.success.main : colors.warning.main, fontSize: 15 }}>
                {emailVerified ? 'Email Verified' : 'Email Not Verified'}
              </Text>
              {!emailVerified && (
                <>
                  <TouchableOpacity
                    style={{ marginLeft: 16, paddingVertical: 4, paddingHorizontal: 10, backgroundColor: colors.button.primary, borderRadius: 5 }}
                    onPress={handleResendVerification}
                    disabled={resendLoading}
                  >
                    <Text style={{ color: 'white', fontSize: 14 }}>
                      {resendLoading ? 'Sending...' : 'Resend Verification'}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={{ marginLeft: 8, paddingVertical: 4, paddingHorizontal: 10, backgroundColor: colors.grey[400], borderRadius: 5 }}
                    onPress={handleCheckVerification}
                  >
                    <Text style={{ color: 'white', fontSize: 14 }}>Check Status</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          )}

          <TouchableOpacity style={styles.button} onPress={handleSaveProfile}>
            <Text style={styles.buttonText}>Save Profile</Text>
          </TouchableOpacity>
          {showProfileSuccess && (
            <Text style={styles.successMessage}>Successfully Updated</Text>
          )}

          <Text style={styles.label}>Current Password</Text>
          <TextInput
            style={styles.input}
            value={currentPassword}
            onChangeText={setCurrentPassword}
            placeholder="Enter current password"
            secureTextEntry
          />
          <Text style={styles.label}>New Password</Text>
          <TextInput
            style={styles.input}
            value={newPassword}
            onChangeText={setNewPassword}
            placeholder="Enter new password"
            secureTextEntry
          />
          <TouchableOpacity style={styles.button} onPress={handleResetPassword}>
            <Text style={styles.buttonText}>Update Password</Text>
          </TouchableOpacity>
          {showPasswordSuccess && (
            <Text style={styles.successMessage}>Successfully Updated</Text>
          )}
        </View>
      </CollapsibleSection>

      <CollapsibleSection title="Appearance">
        <Text style={styles.label}>Theme</Text>
        <Text style={[styles.label, { fontWeight: '400', color: colors.text.secondary, marginTop: 0 }]}>
          Light uses a navy sidebar with a light workspace. Dark applies a darker palette across the app.
        </Text>
        <ThemeModePicker />

        <Text style={[styles.label, { marginTop: 20 }]}>Theme Color</Text>
        <Text style={[styles.label, { fontWeight: '400', color: colors.text.secondary, marginTop: 0 }]}>
          GoalMint is the original green accent. Other colors change buttons, highlights, and accents only.
        </Text>
        <ThemeColorPicker />
      </CollapsibleSection>

      <CollapsibleSection title="Currency & Region">
        <View style={styles.inputContainer}>
          <Text style={styles.label}>Preferred Currency</Text>
          <View style={styles.pickerContainer}>
            {currencyOptions.map((option) => (
              <TouchableOpacity
                key={option.value.code}
                style={[
                  styles.pickerOption,
                  settings.currency.code === option.value.code && styles.pickerOptionSelected,
                ]}
                onPress={() => {
                  if (option.value.code === 'CUSTOM') {
                    setShowCustomCurrencyModal(true);
                  } else {
                    updateCurrency(option.value.code, option.value.symbol);
                  }
                }}
              >
                <Text style={styles.pickerOptionText}>{option.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <Text style={styles.label}>Date Format</Text>
          <View style={styles.pickerContainer}>
            {dateFormatOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.pickerOption,
                  settings.dateFormat === option.value && styles.pickerOptionSelected,
                ]}
                onPress={() => updateDateFormat(option.value)}
              >
                <Text style={styles.pickerOptionText}>{option.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </CollapsibleSection>

      <CollapsibleSection title="Income & Payment Preferences">
        <View style={styles.inputContainer}>
          <Text style={styles.label}>Income Type</Text>
          <View style={styles.pickerContainer}>
            {incomeTypeOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.pickerOption,
                  settings.income.frequency === option.value && styles.pickerOptionSelected,
                ]}
                onPress={() => updateIncomeSettings({ frequency: option.value })}
              >
                <Text style={styles.pickerOptionText}>{option.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={styles.switchContainer}>
            <Text style={styles.label}>Payment Due Reminders</Text>
            <Switch
              value={settings.income.reminders}
              onValueChange={(value) => updateIncomeSettings({ reminders: value })}
              trackColor={{ false: colors.grey[300], true: colors.primary.light }}
              thumbColor={settings.income.reminders ? colors.primary.main : colors.grey[400]}
            />
          </View>
        </View>
      </CollapsibleSection>

      <CollapsibleSection title="Notifications">
        <View style={styles.inputContainer}>
          <View style={styles.switchContainer}>
            <Text style={styles.label}>Goal Progress</Text>
            <Switch
              value={settings.notifications.goalProgress}
              onValueChange={(value) => updateNotifications({ goalProgress: value })}
              trackColor={{ false: colors.grey[300], true: colors.primary.light }}
              thumbColor={settings.notifications.goalProgress ? colors.primary.main : colors.grey[400]}
            />
          </View>
          <View style={styles.switchContainer}>
            <Text style={styles.label}>Deadline Nearing</Text>
            <Switch
              value={settings.notifications.deadlineNearing}
              onValueChange={(value) => updateNotifications({ deadlineNearing: value })}
              trackColor={{ false: colors.grey[300], true: colors.primary.light }}
              thumbColor={settings.notifications.deadlineNearing ? colors.primary.main : colors.grey[400]}
            />
          </View>
          <View style={styles.switchContainer}>
            <Text style={styles.label}>Payment Input</Text>
            <Switch
              value={settings.notifications.paymentInput}
              onValueChange={(value) => updateNotifications({ paymentInput: value })}
              trackColor={{ false: colors.grey[300], true: colors.primary.light }}
              thumbColor={settings.notifications.paymentInput ? colors.primary.main : colors.grey[400]}
            />
          </View>
        </View>
      </CollapsibleSection>

      <CollapsibleSection title="Privacy & Security">
        <View style={styles.inputContainer}>
          <Text style={[styles.label, { fontWeight: '400', color: colors.text.secondary, marginBottom: 12 }]}>
            Choose one: App Lock (PIN) or Fingerprint Unlock. Turning one on turns the other off.
          </Text>

          <View style={settingsLocalStyles.securitySwitchRow}>
            <Text style={settingsLocalStyles.securitySwitchLabel}>App Lock (PIN)</Text>
            <Switch
              value={localAppLock}
              onValueChange={handleAppLockToggle}
              trackColor={{ false: colors.grey[500], true: colors.primary.light }}
              thumbColor={localAppLock ? '#ffffff' : colors.grey[300]}
              ios_backgroundColor={colors.grey[500]}
            />
          </View>

          <View style={settingsLocalStyles.securitySwitchRow}>
            <Text style={settingsLocalStyles.securitySwitchLabel}>Fingerprint Unlock</Text>
            <Switch
              value={localBiometric}
              onValueChange={handleBiometricToggle}
              trackColor={{ false: colors.grey[500], true: colors.primary.light }}
              thumbColor={localBiometric ? '#ffffff' : colors.grey[300]}
              ios_backgroundColor={colors.grey[500]}
            />
          </View>

          {(settings.security.appLock || settings.security.biometric) && (
            <Text style={{ fontSize: 14, color: colors.grey[600], marginBottom: 10 }}>
              After unlocking, you won't need to enter your username/password for 4 weeks.
            </Text>
          )}
          
          {settings.security.appLock && (
            <TouchableOpacity style={styles.button} onPress={() => setShowPinModal(true)}>
              <Text style={styles.buttonText}>{settings.security.pinCode ? 'Change PIN' : 'Set PIN'}</Text>
            </TouchableOpacity>
          )}
          {showPinSuccess && (
            <Text style={styles.successMessage}>PIN updated successfully!</Text>
          )}
        </View>
      </CollapsibleSection>

      <CollapsibleSection title="Subscription">
        <View style={styles.inputContainer}>
          <View style={styles.subscriptionInfo}>
            <Text style={styles.label}>Current Plan</Text>
            <Text style={styles.subscriptionTier}>
              {subscription?.tier ? subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1) : 'Free'}
            </Text>
            {subscription?.status && (
              <Text style={styles.subscriptionStatus}>
                Status: {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
              </Text>
            )}
            {subscription?.endDate && (
              <Text style={styles.subscriptionDate}>
                Expires: {new Date(subscription.endDate).toLocaleDateString()}
              </Text>
            )}
          </View>
          
          <TouchableOpacity 
            style={[styles.button, styles.upgradeButton]} 
            onPress={() => navigation.navigate('Subscription')}
          >
            <Text style={styles.buttonText}>
              {subscription?.tier === 'free' ? 'Upgrade Plan' : 'Manage Subscription'}
            </Text>
          </TouchableOpacity>
        </View>
      </CollapsibleSection>

      <CollapsibleSection title="Export Data">
        {(!subscription || subscription.tier === 'free') ? (
          <View style={{ padding: 16 }}>
            <Text style={{ color: '#757575', fontSize: 16, marginBottom: 12 }}>
              Export data is available for Standard and Premium plans
            </Text>
            <TouchableOpacity
              style={{
                backgroundColor: '#388e3c',
                borderRadius: 6,
                paddingVertical: 10,
                paddingHorizontal: 18,
                alignSelf: 'flex-start',
              }}
              onPress={() => navigation.navigate('Subscription')}
            >
              <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 16 }}>Upgrade Plan</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.pickerContainer}>
            {getExportFormats().includes('csv') && (
              <TouchableOpacity
                style={styles.pickerOption}
                onPress={() => handleExportData('csv')}
              >
                <Text style={styles.pickerOptionText}>Export as CSV</Text>
              </TouchableOpacity>
            )}
            {getExportFormats().includes('excel') && (
              <TouchableOpacity
                style={styles.pickerOption}
                onPress={() => handleExportData('xlsx')}
              >
                <Text style={styles.pickerOptionText}>Export as Excel</Text>
              </TouchableOpacity>
            )}
            {getExportFormats().includes('pdf') && (
              <TouchableOpacity
                style={styles.pickerOption}
                onPress={() => handleExportData('pdf')}
              >
                <Text style={styles.pickerOptionText}>Export as PDF</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </CollapsibleSection>

      <CollapsibleSection title="Support">
        <View style={styles.inputContainer}>
          <TouchableOpacity
            style={styles.button}
            onPress={() => navigation.navigate('FAQ')}
          >
            <Text style={styles.buttonText}>FAQ</Text>
          </TouchableOpacity>
          <Text style={styles.supportText}>Contact Support at sales@expensewatcher.com</Text>
        </View>
      </CollapsibleSection>

      {/* Developer Panel - Only show in development */}
      {hasAccess && (
        <CollapsibleSection title="🔧 Developer Tools" initiallyExpanded={false}>
          <View style={styles.inputContainer}>
            <View style={styles.switchContainer}>
              <Text style={styles.label}>Developer Mode</Text>
              <Switch
                value={isDeveloperMode}
                onValueChange={handleToggleDeveloperMode}
                trackColor={{ false: colors.grey[300], true: colors.primary.light }}
                thumbColor={isDeveloperMode ? colors.primary.main : colors.grey[400]}
              />
            </View>
            
            {isDeveloperMode && (
              <>
                <Text style={styles.label}>Current Tier: {currentTier.toUpperCase()}</Text>
                
                <Text style={styles.label}>Test Subscription Tiers</Text>
                <View style={styles.pickerContainer}>
                  <TouchableOpacity
                    style={[
                      styles.pickerOption,
                      currentTier === 'free' && styles.pickerOptionSelected
                    ]}
                    onPress={() => handleSetTestTier('free')}
                  >
                    <Text style={styles.pickerOptionText}>Free</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.pickerOption,
                      currentTier === 'standard' && styles.pickerOptionSelected
                    ]}
                    onPress={() => handleSetTestTier('standard')}
                  >
                    <Text style={styles.pickerOptionText}>Standard</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.pickerOption,
                      currentTier === 'premium' && styles.pickerOptionSelected
                    ]}
                    onPress={() => handleSetTestTier('premium')}
                  >
                    <Text style={styles.pickerOptionText}>Premium</Text>
                  </TouchableOpacity>
                </View>
                
                <Text style={styles.label}>Test Trial Subscriptions</Text>
                <View style={styles.pickerContainer}>
                  <TouchableOpacity
                    style={[styles.pickerOption, { backgroundColor: colors.success.main }]}
                    onPress={() => handleSetTestTrial('standard')}
                  >
                    <Text style={[styles.pickerOptionText, { color: 'white' }]}>Standard Trial</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.pickerOption, { backgroundColor: colors.button.primary }]}
                    onPress={() => handleSetTestTrial('premium')}
                  >
                    <Text style={[styles.pickerOptionText, { color: 'white' }]}>Premium Trial</Text>
                  </TouchableOpacity>
                </View>
                
                <TouchableOpacity 
                  style={[styles.button, { backgroundColor: colors.warning.main }]} 
                  onPress={handleResetToFree}
                >
                  <Text style={styles.buttonText}>Reset to Free</Text>
                </TouchableOpacity>
                
                <Text style={styles.developerNote}>
                  💡 Use these tools to test different subscription tiers and features.
                  Changes are saved to your account and persist until reset.
                </Text>
                
                <Text style={styles.developerNote}>
                  🧪 Trial buttons create a 7-day trial subscription. Check the Subscription screen to see the trial banner!
                </Text>
              </>
            )}
          </View>
        </CollapsibleSection>
      )}

      <CollapsibleSection title="About">
        <View style={styles.inputContainer}>
          <View style={styles.versionContainer}>
            <Text style={styles.label}>App Version</Text>
            <Text style={styles.versionText}>{APP_VERSION}</Text>
          </View>
          <TouchableOpacity
            style={styles.button}
            onPress={() => navigation.navigate('TermsPrivacy')}
          >
            <Text style={styles.buttonText}>Terms & Privacy</Text>
          </TouchableOpacity>
        </View>
      </CollapsibleSection>

      <CollapsibleSection title="⚠️ Delete Account" initiallyExpanded={false}>
        <View style={{ padding: 16 }}>
          <Text style={{ color: '#d32f2f', fontSize: 16, textAlign: 'center', marginBottom: 16 }}>
            Deleting your account will permanently remove all your data and cannot be undone.
          </Text>
          <TouchableOpacity
            style={{
              backgroundColor: '#d32f2f',
              borderRadius: 8,
              paddingVertical: 16,
              alignItems: 'center',
              width: '100%',
            }}
            onPress={() => setShowDeleteAccountModal(true)}
          >
            <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 18 }}>Delete Account</Text>
          </TouchableOpacity>
        </View>
      </CollapsibleSection>

      {/* Custom Currency Modal */}
      <AppModal
        visible={showCustomCurrencyModal}
        animationType="slide"
        size="md"
        scrollable={false}
        onRequestClose={() => setShowCustomCurrencyModal(false)}
      >
            <Text style={styles.modalTitle}>Custom Currency</Text>
            
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Currency Code (e.g., USD)</Text>
              <TextInput
                style={styles.input}
                value={customCurrencyCode}
                onChangeText={text => setCustomCurrencyCode(text.toUpperCase())}
                maxLength={3}
                placeholder="Enter 3-letter code"
                autoCapitalize="characters"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Currency Symbol (e.g., $)</Text>
              <TextInput
                style={styles.input}
                value={customCurrencySymbol}
                onChangeText={setCustomCurrencySymbol}
                maxLength={3}
                placeholder="Enter currency symbol"
              />
            </View>

            <View style={[styles.modalButtons, isWeb && webModalFooterStyle]}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setShowCustomCurrencyModal(false)}
              >
                <Text style={styles.modalButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={handleCustomCurrencySubmit}
              >
                <Text style={styles.modalButtonText}>Save</Text>
              </TouchableOpacity>
            </View>
      </AppModal>

      {/* Delete Account Confirmation Modal */}
      <AppModal
        visible={showDeleteAccountModal}
        animationType="slide"
        size="md"
        scrollable={false}
        onRequestClose={() => setShowDeleteAccountModal(false)}
      >
            <Text style={styles.modalTitle}>Delete Account</Text>
            
            <Text style={styles.modalWarning}>
              ⚠️ This action cannot be undone. All your goals and data will be permanently deleted.
            </Text>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Enter your password to confirm</Text>
              <TextInput
                style={styles.input}
                value={deleteAccountPassword}
                onChangeText={setDeleteAccountPassword}
                placeholder="Enter your password"
                secureTextEntry
                autoCapitalize="none"
              />
            </View>

            <View style={[styles.modalButtons, isWeb && webModalFooterStyle]}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => {
                  setShowDeleteAccountModal(false);
                  setDeleteAccountPassword('');
                }}
                disabled={isDeleting}
              >
                <Text style={[styles.buttonText, { color: 'white' }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.deleteButton]}
                onPress={handleDeleteAccount}
                disabled={isDeleting}
              >
                <Text style={styles.modalButtonText}>
                  {isDeleting ? 'Deleting...' : 'Delete Account'}
                </Text>
              </TouchableOpacity>
            </View>
      </AppModal>

      <AppModal
        visible={showPinModal}
        size="sm"
        scrollable={false}
        onRequestClose={closePinModal}
      >
            <Text style={styles.modalTitle}>Set Your PIN</Text>
            <TextInput
              style={styles.input}
              placeholder="Enter 4-digit PIN"
              keyboardType="numeric"
              secureTextEntry
              maxLength={4}
              value={pinCode}
              onChangeText={setPinCode}
            />
            <View style={[styles.modalButtonContainer, isWeb && webModalFooterStyle]}>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton]}
                onPress={closePinModal}
              >
                <Text style={styles.buttonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.button, styles.saveButton]} onPress={handleSetPin}>
                <Text style={styles.buttonText}>Save PIN</Text>
              </TouchableOpacity>
            </View>
      </AppModal>

      {successMessage && <Text style={styles.successText}>{successMessage}</Text>}

      {/* Re-authentication Modal for Email Update */}
      <AppModal
        visible={showReauthModal}
        animationType="slide"
        size="md"
        scrollable={false}
        onRequestClose={() => {
          setShowReauthModal(false);
          setPendingEmail(null);
          setReauthPassword('');
        }}
      >
            <Text style={styles.modalTitle}>Re-authenticate to Update Email</Text>
            <Text style={{ marginBottom: 12 }}>
              For security, please enter your current password to update your email address.
            </Text>
            <TextInput
              style={styles.input}
              value={reauthPassword}
              onChangeText={setReauthPassword}
              placeholder="Enter your password"
              secureTextEntry
              autoCapitalize="none"
            />
            <View style={[styles.modalButtons, isWeb && webModalFooterStyle]}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => {
                  setShowReauthModal(false);
                  setPendingEmail(null);
                  setReauthPassword('');
                }}
                disabled={reauthLoading}
              >
                <Text style={styles.modalButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={handleReauthAndUpdateEmail}
                disabled={reauthLoading}
              >
                <Text style={styles.modalButtonText}>{reauthLoading ? 'Updating...' : 'Update Email'}</Text>
              </TouchableOpacity>
            </View>
      </AppModal>

      {/* Ad Banner - continuously shown for non-subscribers */}
      <AdBanner />
    </PageScroll>
    </WebPage>
  );
};

export default SettingsScreen; 